import React, { useState, useEffect } from 'react'
import Header from './header'
import CustomerHeader from '../customer/CustomerHeader'
import Footer from './Footer'
import Newsletter from './HomeNewsletter'
import ServiceMap from './ServiceMap'
import { Link } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { API_BASE_URL } from '../../utils/api'
import { Search, MapPin, Star, Shield, Wrench, Grid, List, ArrowRight, Briefcase, Clock, SlidersHorizontal, Car, Sparkles, Utensils, Droplet, Hammer, Laptop, Scale, Truck, Heart, Dog, Calendar } from 'lucide-react'

interface Service {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  image?: string;
  createdAt: string;
  worker: {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    profilePicture: string;
    parish: string;
    rating: number;
    totalReviews: number;
    experienceYears: number;
    bio: string;
    hourlyRate: number;
  };
}

interface Skill {
  id: number;
  name: string;
  description: string;
  category_id: number;
  subcategory_id?: number;
  status: 'active' | 'inactive';
  category_name?: string;
  subcategory_name?: string;
  talent_count?: number;
}

function Listing() {
  const { isAuthenticated } = useCustomerAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Rating filter state
  const [selectedRating, setSelectedRating] = useState<number>(0);

  // Category filter state
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Skill filter state
  const [selectedSkill, setSelectedSkill] = useState<string>('');

  // Search input filters
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('top_rated');

  useEffect(() => {
    fetchServices();
    fetchCategories();
    fetchSkills();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/services`);
      const data = await response.json();
      console.log(data, 'data');
      if (data.success) {
        setAllServices(data.services);
        setServices(data.services);
      } else {
        setError(data.message || 'Failed to fetch services');
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/services/categories`);
      const data = await response.json();

      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/skills/public/active`);
      const data = await response.json();

      if (data.success) {
        setSkills(data.skills);
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
    }
  };

  // Filter services based on rating, category, skill, searchQuery, and locationQuery
  useEffect(() => {
    let filtered = [...allServices];

    // Filter by rating
    if (selectedRating > 0) {
      filtered = filtered.filter(service =>
        service.worker.rating >= selectedRating
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(service => service.category === selectedCategory);
    }

    // Filter by skill
    if (selectedSkill) {
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(selectedSkill.toLowerCase()) ||
        service.category.toLowerCase().includes(selectedSkill.toLowerCase()) ||
        service.description.toLowerCase().includes(selectedSkill.toLowerCase())
      );
    }

    // Filter by search text query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(q) ||
        service.category.toLowerCase().includes(q) ||
        service.description.toLowerCase().includes(q) ||
        (service.worker.name && service.worker.name.toLowerCase().includes(q))
      );
    }

    // Filter by location query
    if (locationQuery) {
      const loc = locationQuery.toLowerCase();
      filtered = filtered.filter(service =>
        service.worker.parish && service.worker.parish.toLowerCase().includes(loc)
      );
    }

    setServices(filtered);
  }, [allServices, selectedRating, selectedCategory, selectedSkill, searchQuery, locationQuery]);

  const BASE_URL = (() => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace(/\/api$/, '');
    }
    if (typeof window !== 'undefined') {
      const h = window.location.hostname;
      const isLocal =
        h === 'localhost' || h === '127.0.0.1' ||
        /^192\.168\./.test(h) || /^10\./.test(h) ||
        /^172\.(1[6-9]|2[0-9]|3[01])\./.test(h);
      if (isLocal) return `http://${h}:5000`;
    }
    return 'https://api.skillseek.co';
  })();

  const normalizeImageUrl = (imagePath: string | null | undefined, fallback: string): string => {
    if (!imagePath) return fallback;
    
    let cleanPath = imagePath;
    const uploadsIndex = cleanPath.indexOf('/uploads/');
    if (uploadsIndex !== -1 && (cleanPath.startsWith('http://') || cleanPath.startsWith('https://'))) {
      cleanPath = cleanPath.substring(uploadsIndex + 1);
    }
    
    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) return cleanPath;
    if (cleanPath.startsWith('/uploads/') || cleanPath.startsWith('uploads/')) {
      return `${BASE_URL}/${cleanPath.replace(/^\//, '')}`;
    }
    return `${BASE_URL}/uploads/${cleanPath}`;
  };

  // Handle rating change
  const handleRatingChange = (rating: number) => {
    setSelectedRating(rating);
  };

  // Handle category change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSkill('');
    setSelectedRating(0);
    setSearchQuery('');
    setLocationQuery('');
  };

  const getCategoryIcon = (category: string) => {
    const cat = category.toLowerCase();
    if (cat.includes('automotive')) return <Car size={16} />;
    if (cat.includes('beauty') || cat.includes('wellness')) return <Sparkles size={16} />;
    if (cat.includes('catering') || cat.includes('food')) return <Utensils size={16} />;
    if (cat.includes('cleaning')) return <Droplet size={16} />;
    if (cat.includes('construction')) return <Hammer size={16} />;
    if (cat.includes('event')) return <Calendar size={16} />;
    if (cat.includes('home')) return <Wrench size={16} />;
    if (cat.includes('it') || cat.includes('technology') || cat.includes('information')) return <Laptop size={16} />;
    if (cat.includes('legal')) return <Scale size={16} />;
    if (cat.includes('moving') || cat.includes('storage')) return <Truck size={16} />;
    if (cat.includes('nursing') || cat.includes('health')) return <Heart size={16} />;
    if (cat.includes('pet')) return <Dog size={16} />;
    
    return <Briefcase size={16} />;
  };

  const mainServices = [...services].sort((a, b) => {
    if (sortOrder === 'price_asc') return a.price - b.price;
    if (sortOrder === 'price_desc') return b.price - a.price;
    if (sortOrder === 'most_reviews') return (b.worker.totalReviews || 0) - (a.worker.totalReviews || 0);
    if (sortOrder === 'newest') return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
    return (b.worker.rating || 0) - (a.worker.rating || 0); // top_rated default
  });
  const apiKey = 'AIzaSyD5ZwymW1HDabeyBUPwgTaZHoIWmLNvCFA';

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Conditionally render header based on authentication */}
      {isAuthenticated ? <CustomerHeader /> : <Header />}

      {/* Services Hero Section */}
      <section className="premium-services-hero">
        <span className="premium-services-hero-badge">Browse services</span>
        <h1 className="premium-services-hero-title">Find the right professional, first time.</h1>
        <p className="premium-services-hero-subtitle">
          Search across categories, skills, and parishes to shortlist trusted pros in seconds.
        </p>

        {/* Search Pill Card */}
        <div className="premium-search-pill-card" style={{ marginTop: '32px', maxWidth: '768px' }}>
          {/* Service Search Input */}
          <div className="premium-search-field">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search for a service, skill, or provider"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* Divider */}
          <div className="premium-search-divider"></div>

          {/* Location Search Input */}
          <div className="premium-search-field">
            <MapPin size={20} />
            <input
              type="text"
              placeholder="Parish or city"
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* Search Button */}
          <button 
            type="button"
            className="premium-search-btn"
            style={{ minWidth: '120px' }}
          >
            Search
          </button>
        </div>
      </section>

      {/* Main Listing Section */}
      <div className="premium-listing-page">
        {/* Mobile/Tablet Filter Toggle Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0' }}>
          <button
            type="button"
            className="premium-filter-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: '#185FA5',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 20px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: 'pointer',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <SlidersHorizontal size={16} style={{ marginRight: '6px' }} />
            {sidebarOpen ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {/* Sidebar Filters */}
        <aside className={`premium-filter-sidebar${sidebarOpen ? ' sidebar-visible' : ' sidebar-hidden'}`}>
          <div className="premium-filter-header">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SlidersHorizontal size={18} />
              Filters
            </h2>
            <button
              type="button"
              onClick={clearFilters}
              className="premium-clear-btn"
            >
              Clear all
            </button>
          </div>

          {/* Categories Filter */}
          <div className="premium-filter-section">
            <h3>Category</h3>
            <ul className="premium-category-list">
              <li>
                <button
                  type="button"
                  className={`premium-category-item ${selectedCategory === '' ? 'active' : ''}`}
                  onClick={() => handleCategoryChange('')}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Grid size={16} />
                    All Categories
                  </span>
                  <span className="premium-category-count">{allServices.length}</span>
                </button>
              </li>
              {categories.map((category) => {
                const count = allServices.filter(s => s.category === category).length;
                return (
                  <li key={category}>
                    <button
                      type="button"
                      className={`premium-category-item ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(category)}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getCategoryIcon(category)}
                        {category}
                      </span>
                      <span className="premium-category-count">{count}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>



          {/* Minimum Rating Filter */}
          <div className="premium-filter-section">
            <h3 style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.05em', color: '#9CA3AF' }}>Minimum rating</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {[
                { label: 'Any rating', value: 0, stars: 0 },
                { label: ' & up', value: 1, stars: 1 },
                { label: ' & up', value: 2, stars: 2 },
                { label: ' & up', value: 3, stars: 3 },
                { label: ' & up', value: 4, stars: 4 },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`premium-rating-option ${selectedRating === opt.value ? 'active' : ''}`}
                  onClick={() => handleRatingChange(opt.value)}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {opt.stars > 0 ? (
                      <span style={{ display: 'inline-flex', gap: '2px', marginRight: '6px' }}>
                        {Array.from({ length: opt.stars }).map((_, i) => (
                          <Star 
                            key={i} 
                            size={14} 
                            fill="#EF9F27" 
                            stroke="#EF9F27" 
                          />
                        ))}
                      </span>
                    ) : (
                      <span>{opt.label}</span>
                    )}
                    {opt.stars > 0 && <span>& up</span>}
                  </span>
                  {selectedRating === opt.value && (
                    <span 
                      style={{ 
                        width: '6px', 
                        height: '6px', 
                        backgroundColor: '#EF9F27', 
                        borderRadius: '50%',
                        marginLeft: '8px'
                      }}
                    ></span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Listing Grid */}
        <main className="premium-listing-main">
          {/* Top Info Bar */}
          <div className="premium-listing-top-bar">
            <div className="premium-listing-count">
              {services.length}
              <span>services available</span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{ outline: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer' }}
              >
                <option value="top_rated">Top Rated</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="most_reviews">Most Reviews</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          </div>

          {/* Services Grid */}
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading services...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center p-5 text-red-500">
              <p>{error}</p>
            </div>
          ) : mainServices.length > 0 ? (
            <div className="premium-services-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {mainServices.map((service) => (
                <div 
                  key={service.id} 
                  className="premium-service-card"
                  style={{ padding: '20px', minHeight: '380px' }}
                >
                  <div>
                    {/* Image / Fallback Center Box with absolute category badge */}
                    <div style={{ position: 'relative' }}>
                      {service.image ? (
                        <div className="premium-card-image-wrapper" style={{ marginTop: 0 }}>
                          <img 
                            src={normalizeImageUrl(service.image, '/assets/images/sp-one.png')} 
                            alt={service.title} 
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/assets/images/sp-one.png';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="premium-card-icon-wrapper" style={{ marginTop: 0 }}>
                          <Briefcase size={36} strokeWidth={1.5} />
                        </div>
                      )}
                      
                      {/* Absolute Category Badge */}
                      <span 
                        className="premium-card-tag" 
                        style={{ 
                          position: 'absolute', 
                          top: '12px', 
                          left: '12px', 
                          zIndex: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                      >
                        {service.category}
                      </span>
                    </div>

                    {/* Title & Rating inline */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '16px', gap: '12px' }}>
                      <Link to={`/detail/${service.id}`} style={{ textDecoration: 'none', flex: 1 }}>
                        <h4 className="premium-service-card-title" style={{ marginTop: 0, marginBottom: 0 }}>
                          {service.title}
                        </h4>
                      </Link>
                      <div className="premium-rating-badge">
                        <Star size={12} fill="#D97706" stroke="#D97706" />
                        <span>{service.worker.rating ? service.worker.rating.toFixed(1) : '0.0'}</span>
                      </div>
                    </div>

                    {/* Provider name */}
                    <span className="premium-service-author">
                      by {service.worker.name || `${service.worker.firstName} ${service.worker.lastName}`}
                    </span>
                    
                    {/* Description */}
                    <p className="premium-service-desc">{service.description}</p>
                    
                    {/* Badges with inline icons */}
                    <div className="premium-service-badges">
                      <span className="premium-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={10} />
                        {service.worker.parish || 'Jamaica'}
                      </span>
                      <span className="premium-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={10} />
                        {service.duration || 'Flexible'}
                      </span>
                      <span className="premium-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Briefcase size={10} />
                        {service.worker.experienceYears || '0'} yrs
                      </span>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="premium-service-footer">
                    <div>
                      <span className="premium-price-label">Starting at</span>
                      <span className="premium-price-val">${service.price}</span>
                    </div>
                    <Link to={`/detail/${service.id}`} className="premium-view-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      View <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-5 bg-white border border-gray-100 rounded-2xl">
              <h5 className="text-gray-500">No services found</h5>
              <p className="text-gray-400 text-sm">Try resetting filters or searching for different keywords</p>
            </div>
          )}
        </main>
      </div>

      <Newsletter />
      <Footer />
    </div>
  )
}

export default Listing;
